import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState,
} from "react";
import { toast } from "sonner";
import {
	type AutoCombineEntryV2,
	entryGroupsQueryKey,
	useEntryFiltersCache,
} from "@/shared/cache";
import type { Entry } from "@/shared/types/entries";
import { legacyRouteStubError } from "@/shared/utils/legacyRouteStubs";
import { useApi } from "@/shared/utils/request";
import { EntryMutationOutcomeError } from "@/v2/hooks/actions/actionTypes";
import type { AddPayload } from "@/v2/hooks/actions/add";
import { createAddSubmissionGate } from "@/v2/hooks/actions/addSubmission";
import type { CollapseOptions } from "@/v2/hooks/actions/collapse";
import {
	type EditPayload,
	toUpdateBody,
	toUpdateEntryGroupBody,
} from "@/v2/hooks/actions/edit";
import {
	durationToUnitsSeconds,
	formatEntryId,
	parseEntryId,
} from "@/v2/hooks/actions/entryId";
import type { ExportResult } from "@/v2/hooks/actions/export";
import type { GroupOptions } from "@/v2/hooks/actions/group";
import type { MergeOptions } from "@/v2/hooks/actions/merge";
import type { SetStatusTarget } from "@/v2/hooks/actions/setStatus";
import {
	ApiError,
	type ApiError as ApiErrorType,
	actingHeaders,
	plural,
} from "@/v2/hooks/actions/types";
import {
	resolveNewEntryGroupFields,
	uniqueSelectedSourceIds,
} from "@/v2/lib/entryGroupSelection";
import { useEntriesUndo } from "@/v2/undo";

type ExportTarget = {
	id: number;
	entryGroupId?: string | null;
};

type EntryGroupExportOutcome = {
	entryGroupId: string;
	status: "exported" | "failed";
	error: { message?: string } | null;
};

const AFFECTED_ENTRY_GROUPS_QUERY = { includeAffectedEntryGroups: true };

type ActionsContextType = {
	deleteEntries: (ids: number[], timekeeperId?: string | null) => void;
	editEntry: (entry: EditPayload) => void;
	editThenExport: (entry: EditPayload) => void;
	addEntry: (entry: AddPayload, onSuccess?: () => void) => void;
	isAddingEntry: boolean;
	duplicateEntries: (ids: number[], timekeeperId?: string | null) => void;
	groupEntries: (
		ids: number[],
		options: GroupOptions,
		onApiError?: (err: ApiErrorType) => void,
		timekeeperId?: string | null,
	) => void;
	mergeEntries: (
		ids: number[],
		options: MergeOptions,
		onApiError?: (err: ApiErrorType) => void,
		timekeeperId?: string | null,
	) => void;
	collapseEntries: (
		ids: number[],
		mainSourceId: number,
		options: CollapseOptions,
		onApiError?: (err: ApiErrorType) => void,
		timekeeperId?: string | null,
	) => void;
	moveEntriesToNewGroup: (
		entries: Entry[],
		timekeeperId?: string | null,
	) => void;
	exportEntries: (
		entries: ExportTarget[],
		timekeeperId?: string | null,
	) => void;
	resolveFeedback: (entryId: number) => void;
	setStatus: (
		id: number,
		target: SetStatusTarget,
		onApiError?: (err: ApiErrorType) => void,
		timekeeperId?: string | null,
	) => void;
	setStatusMany: (
		ids: number[],
		target: SetStatusTarget,
		onApiError?: (err: ApiErrorType) => void,
		timekeeperId?: string | null,
	) => void;
	failedExports: Map<number, string>;
	pendingEntryIds: Set<number>;
	queueSize: number;
};

type MutationSpec = {
	ids: number[];
	clearFailedExportIds?: number[];
	mutationFn: () => Promise<unknown>;
	toast: {
		loading: string;
		success: string;
		error: string | ((err: unknown) => string);
	};
	onSuccess?: () => void;
	onError?: (err: unknown) => void;
	onSettled?: (succeeded: boolean) => void;
};

type MutationContext = {
	spec: MutationSpec;
	toastId: string | number;
};

const ActionsContext = createContext<ActionsContextType | undefined>(undefined);

function stripUndefined<T extends object>(obj: T): Partial<T> {
	return Object.fromEntries(
		Object.entries(obj).filter(([, value]) => value !== undefined),
	) as Partial<T>;
}

type EntryGroupCandidate = {
	id?: unknown;
	entries?: Array<{ id?: unknown }>;
	miniTransactions?: Array<{ id?: unknown }>;
	mini_transactions?: Array<{ id?: unknown }>;
};

function isEntryGroupCandidate(value: unknown): value is EntryGroupCandidate {
	return (
		typeof value === "object" &&
		value !== null &&
		typeof (value as EntryGroupCandidate).id === "string" &&
		((value as EntryGroupCandidate).id as string).startsWith("eg_")
	);
}

function collectEntryGroupCandidates(data: unknown): EntryGroupCandidate[] {
	const groups: EntryGroupCandidate[] = [];
	const visit = (value: unknown) => {
		if (!value) return;
		if (Array.isArray(value)) {
			for (const item of value) visit(item);
			return;
		}
		if (typeof value !== "object") return;
		if (isEntryGroupCandidate(value)) groups.push(value);
		const record = value as Record<string, unknown>;
		visit(record.data);
		visit(record.affectedEntryGroups);
		visit(record.affected_entry_groups);
		visit(record.entryGroups);
		visit(record.entry_groups);
	};
	visit(data);
	return groups;
}

function groupContainsEntry(
	group: EntryGroupCandidate,
	entryId: string,
): boolean {
	const children = [
		...(group.entries ?? []),
		...(group.miniTransactions ?? []),
		...(group.mini_transactions ?? []),
	];
	return children.some((child) => child.id === entryId);
}

function addedEntryGroupIdFromResponse(
	data: unknown,
	entryId: number | null,
): string | null {
	if (entryId == null) return null;
	const entryWireId = formatEntryId(entryId);
	const groups = collectEntryGroupCandidates(data);
	const matchingGroup = groups.find((group) =>
		groupContainsEntry(group, entryWireId),
	);
	if (typeof matchingGroup?.id === "string") return matchingGroup.id;
	return groups.length === 1 && typeof groups[0].id === "string"
		? groups[0].id
		: null;
}

function firstEntryIdFromResponse(data: unknown): number | null {
	let found: number | null = null;
	const visit = (value: unknown) => {
		if (found !== null || !value) return;
		if (Array.isArray(value)) {
			for (const item of value) visit(item);
			return;
		}
		if (typeof value !== "object") return;
		const record = value as Record<string, unknown>;
		if (typeof record.id === "string" && record.id.startsWith("ent_")) {
			try {
				found = parseEntryId(record.id);
				return;
			} catch {
				// Keep searching so a malformed sibling cannot discard a committed result.
			}
		}
		visit(record.data);
		visit(record.entries);
		visit(record.affectedEntryGroups);
	};
	visit(data);
	return found;
}

function editRequestFor(entry: EditPayload) {
	if (entry.entryGroupId) {
		return {
			endpoint: `/v2/entry-groups/${entry.entryGroupId}`,
			body: toUpdateEntryGroupBody(entry),
		};
	}
	return {
		endpoint: `/v2/entries/${formatEntryId(entry.id)}`,
		body: toUpdateBody(entry),
	};
}

function errorMessage(
	err: unknown,
	fallback: string | ((err: unknown) => string),
): string {
	return typeof fallback === "function" ? fallback(err) : fallback;
}

export function EntriesActionsProvider({ children }: { children: ReactNode }) {
	const queryClient = useQueryClient();
	const { makeApiRequest } = useApi();
	const { entryFilters } = useEntryFiltersCache();
	const { invalidateExportedGroups, runEntryMutation } = useEntriesUndo();
	const [failedExports, setFailedExports] = useState<Map<number, string>>(
		new Map(),
	);
	const [pendingEntryIds, setPendingEntryIds] = useState<Set<number>>(
		new Set(),
	);
	const addSubmissionGate = useRef(createAddSubmissionGate());
	const [isAddingEntry, setIsAddingEntry] = useState(false);

	const clearFailedExports = useCallback((ids: number[]) => {
		setFailedExports((prev) => {
			if (!ids.some((id) => prev.has(id))) return prev;
			const next = new Map(prev);
			for (const id of ids) next.delete(id);
			return next;
		});
	}, []);

	const handleExportComplete = useCallback((result: ExportResult) => {
		setFailedExports((prev) => {
			const next = new Map(prev);
			for (const id of result.exportedIds) next.delete(id);
			for (const { id, error } of result.failedEntries) next.set(id, error);
			return next;
		});
	}, []);

	const recordExportFailure = useCallback((id: number, error: string) => {
		setFailedExports((prev) => {
			const next = new Map(prev);
			next.set(id, error);
			return next;
		});
	}, []);

	const apiCall = useCallback(
		async (opts: Parameters<typeof makeApiRequest>[0]) => {
			const resp = await makeApiRequest(opts);
			if (resp.status === "ERROR") {
				if (resp.apiError) throw new ApiError(resp.apiError);
				throw new Error(resp.error || "Request failed");
			}
			return resp;
		},
		[makeApiRequest],
	);

	const resolveTaskCodeId = useCallback(
		(code: string): string | null | undefined => {
			if (!code) return null;
			const option = entryFilters.taskCode.find(
				(candidate) =>
					candidate.code === code ||
					candidate.label === code ||
					String(candidate.value) === code,
			);
			return option ? String(option.value) : undefined;
		},
		[entryFilters.taskCode],
	);

	const resolveActivityCodeId = useCallback(
		(code: string): string | null | undefined => {
			if (!code) return null;
			const option = entryFilters.activityCode.find(
				(candidate) =>
					candidate.code === code ||
					candidate.label === code ||
					String(candidate.value) === code,
			);
			return option ? String(option.value) : undefined;
		},
		[entryFilters.activityCode],
	);

	const getCachedEntryGroups = useCallback(
		() =>
			queryClient
				.getQueriesData<AutoCombineEntryV2[]>({
					queryKey: entryGroupsQueryKey,
				})
				.flatMap(([, value]) => value ?? []),
		[queryClient],
	);

	const entryGroupsForLegacyIds = useCallback(
		(ids: number[]): string[] => {
			const wanted = new Set(ids);
			const groupIds: string[] = [];
			for (const row of getCachedEntryGroups()) {
				const sourceId = row.miniTransactions[0]?.id;
				const legacyId = sourceId?.startsWith("ent_")
					? parseEntryId(sourceId)
					: 0;
				if (wanted.has(legacyId)) groupIds.push(row.id);
			}
			return [...new Set(groupIds)];
		},
		[getCachedEntryGroups],
	);

	const entryIdsForLegacyIds = useCallback(
		(ids: number[]): string[] => ids.map(formatEntryId),
		[],
	);

	const entryGroupRowsForEntries = useCallback(
		(entries: Entry[]): AutoCombineEntryV2[] => {
			const rows = getCachedEntryGroups();
			const byGroupId = new Map(rows.map((row) => [row.id, row]));
			const byLegacyId = new Map<number, AutoCombineEntryV2>();
			for (const row of rows) {
				const sourceId = row.miniTransactions[0]?.id;
				if (!sourceId?.startsWith("ent_")) continue;
				byLegacyId.set(parseEntryId(sourceId), row);
			}

			const selectedRows: AutoCombineEntryV2[] = [];
			const seen = new Set<string>();
			for (const entry of entries) {
				const row =
					(entry.entryGroupId
						? byGroupId.get(entry.entryGroupId)
						: undefined) ?? byLegacyId.get(entry.id);
				if (!row || seen.has(row.id)) continue;
				seen.add(row.id);
				selectedRows.push(row);
			}
			return selectedRows;
		},
		[getCachedEntryGroups],
	);

	const executeExport = useCallback(
		async (entries: ExportTarget[], timekeeperId?: string | null) => {
			const ids = entries.map((entry) => entry.id);
			const missingEntryGroupIds = entries.filter(
				(entry) => !entry.entryGroupId,
			);
			if (missingEntryGroupIds.length > 0) {
				const message = "Entry group id missing; refresh and try again";
				for (const { id } of missingEntryGroupIds)
					recordExportFailure(id, message);
				handleExportComplete({
					exportedIds: [],
					failedEntries: missingEntryGroupIds.map(({ id }) => ({
						id,
						error: message,
					})),
				});
				throw new Error(message);
			}

			const idsByEntryGroupId = new Map<string, number[]>();
			for (const { id, entryGroupId } of entries) {
				if (!entryGroupId) continue;
				const groupIds = idsByEntryGroupId.get(entryGroupId) ?? [];
				groupIds.push(id);
				idsByEntryGroupId.set(entryGroupId, groupIds);
			}
			const entryGroupIds = Array.from(idsByEntryGroupId.keys());

			const resp = await makeApiRequest({
				endpoint: "/v2/entry-groups/export",
				method: "POST",
				queryParams: AFFECTED_ENTRY_GROUPS_QUERY,
				body: { entryGroupIds },
				headers: actingHeaders(timekeeperId),
			});
			if (resp.status === "ERROR") {
				const err = resp.apiError
					? new ApiError(resp.apiError)
					: new Error(resp.error || "Failed to export entries");
				for (const id of ids) recordExportFailure(id, err.message);
				handleExportComplete({
					exportedIds: [],
					failedEntries: ids.map((id) => ({ id, error: err.message })),
				});
				throw err;
			}
			const outcomes = (resp.data?.data ?? []) as EntryGroupExportOutcome[];
			const exportedIds: number[] = [];
			const exportedEntryGroupIds: string[] = [];
			const failedEntries: { id: number; error: string }[] = [];
			for (const outcome of outcomes) {
				const rowIds = idsByEntryGroupId.get(outcome.entryGroupId) ?? [];
				if (outcome.status === "exported") {
					exportedIds.push(...rowIds);
					exportedEntryGroupIds.push(outcome.entryGroupId);
					continue;
				}

				const error = outcome.error?.message ?? "Failed to export entry";
				for (const id of rowIds) failedEntries.push({ id, error });
			}
			handleExportComplete({ exportedIds, failedEntries });
			if (failedEntries.length > 0) {
				const err = new EntryMutationOutcomeError(
					failedEntries.length === 1
						? failedEntries[0].error
						: `${failedEntries.length} entries failed: ${[
								...new Set(failedEntries.map((f) => f.error)),
							].join("; ")}`,
					{ exportedEntryGroupIds },
				);
				Object.assign(err, { failedEntries });
				throw err;
			}
			return exportedEntryGroupIds;
		},
		[handleExportComplete, makeApiRequest, recordExportFailure],
	);

	const mutation = useMutation<unknown, unknown, MutationSpec, MutationContext>(
		{
			mutationFn: (spec) => runEntryMutation(spec.mutationFn),
			onMutate: async (spec) => {
				await queryClient.cancelQueries({ queryKey: entryGroupsQueryKey });
				if (spec.clearFailedExportIds)
					clearFailedExports(spec.clearFailedExportIds);
				setPendingEntryIds((prev) => {
					const next = new Set(prev);
					for (const id of spec.ids) next.add(id);
					return next;
				});
				return { spec, toastId: toast.loading(spec.toast.loading) };
			},
			onError: async (err, _spec, ctx) => {
				if (err instanceof EntryMutationOutcomeError) {
					await invalidateExportedGroups(err.exportedEntryGroupIds);
				}
				ctx?.spec.onError?.(err);
				toast.error(
					errorMessage(err, ctx?.spec.toast.error ?? "Request failed"),
					{
						id: ctx?.toastId,
					},
				);
			},
			onSuccess: async (result, _spec, ctx) => {
				const exportedEntryGroupIds =
					result &&
					typeof result === "object" &&
					"exportedEntryGroupIds" in result
						? ((result as { exportedEntryGroupIds?: string[] })
								.exportedEntryGroupIds ?? [])
						: [];
				await invalidateExportedGroups(exportedEntryGroupIds);
				ctx?.spec.onSuccess?.();
				toast.success(ctx?.spec.toast.success ?? "Done", { id: ctx?.toastId });
			},
			onSettled: (_data, _err, spec) => {
				spec.onSettled?.(_err == null);
				void queryClient.invalidateQueries({ queryKey: entryGroupsQueryKey });
				setPendingEntryIds((prev) => {
					const next = new Set(prev);
					for (const id of spec.ids) next.delete(id);
					return next;
				});
			},
		},
	);

	const run = useCallback(
		(spec: MutationSpec) => {
			mutation.mutate(spec);
		},
		[mutation],
	);

	const addEntry = useCallback(
		(entry: AddPayload, onSuccess?: () => void) => {
			const idempotencyKey = addSubmissionGate.current.begin(entry);
			if (!idempotencyKey) return;
			setIsAddingEntry(true);
			let newEntryId: number | null = null;
			let newEntryGroupId: string | null = entry.entryGroupId ?? null;
			run({
				ids: [],
				mutationFn: async () => {
					const durationSeconds = durationToUnitsSeconds(entry.duration);
					// Stamp the current local time-of-day (pinned to UTC like every
					// entry) so a manual add reads the time it was added, not 12:00 AM.
					const now = new Date();
					const pad = (n: number) => String(n).padStart(2, "0");
					const timeOfDay = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
					const startTime = `${entry.date}T${timeOfDay}.000Z`;
					const endTime = new Date(
						new Date(startTime).getTime() + durationSeconds * 1000,
					).toISOString();
					const matterId = entry.matter_id || null;
					const request = entry.entryGroupId
						? {
								endpoint: "/v2/entries",
								body: {
									data: [
										{
											entryGroupId: entry.entryGroupId,
											matterId,
											startTime,
											endTime,
											description: entry.description,
											durationSeconds,
										},
									],
								},
							}
						: {
								endpoint: "/v2/entry-groups",
								body: {
									data: [
										stripUndefined({
											status: "tracked" as const,
											type: "group" as const,
											matterId,
											taskCodeId: resolveTaskCodeId(entry.task),
											activityCodeId: resolveActivityCodeId(entry.activity),
											narrative: entry.description,
											entries: [
												{
													startTime,
													endTime,
													matterId,
													durationSeconds,
												},
											],
										}),
									],
								},
							};
					const createResult = await apiCall({
						endpoint: request.endpoint,
						method: "POST",
						queryParams: AFFECTED_ENTRY_GROUPS_QUERY,
						body: request.body,
						headers: {
							...actingHeaders(entry.timekeeperId),
							"Idempotency-Key": idempotencyKey,
						},
					});
					newEntryId = firstEntryIdFromResponse(createResult.data);
					newEntryGroupId =
						entry.entryGroupId ??
						addedEntryGroupIdFromResponse(createResult.data, newEntryId);
					if (!entry.shouldExport) return createResult;
					if (entry.shouldExport) {
						if (newEntryId == null)
							throw new EntryMutationOutcomeError("No entry ID returned", {
								committedResult: createResult,
							});
						if (!newEntryGroupId)
							throw new EntryMutationOutcomeError(
								"No entry group ID returned",
								{
									committedResult: createResult,
								},
							);
						try {
							const exportedEntryGroupIds = await executeExport(
								[{ id: newEntryId, entryGroupId: newEntryGroupId }],
								entry.timekeeperId,
							);
							return { exportedEntryGroupIds };
						} catch (err) {
							const exportedEntryGroupIds =
								err instanceof EntryMutationOutcomeError
									? err.exportedEntryGroupIds
									: [];
							throw new EntryMutationOutcomeError(
								err instanceof Error ? err.message : "Export failed",
								{ committedResult: createResult, exportedEntryGroupIds },
							);
						}
					}
				},
				toast: {
					loading: "Adding entry…",
					success: entry.shouldExport
						? "Entry added and exported"
						: "Entry added",
					error: (err) =>
						entry.shouldExport && err instanceof EntryMutationOutcomeError
							? "Entry added but export failed"
							: "Failed to add entry",
				},
				onSuccess: () => onSuccess?.(),
				onSettled: (succeeded) => {
					addSubmissionGate.current.finish(succeeded);
					setIsAddingEntry(false);
				},
			});
		},
		[apiCall, executeExport, resolveActivityCodeId, resolveTaskCodeId, run],
	);

	const editEntry = useCallback(
		(entry: EditPayload) => {
			run({
				ids: [entry.id],
				clearFailedExportIds: [entry.id],
				mutationFn: () =>
					apiCall({
						endpoint: editRequestFor(entry).endpoint,
						method: "PATCH",
						queryParams: AFFECTED_ENTRY_GROUPS_QUERY,
						body: editRequestFor(entry).body,
						headers: actingHeaders(entry.timekeeperId),
					}),
				toast: {
					loading: "Saving changes…",
					success: "Entry saved",
					error: "Failed to save entry",
				},
			});
		},
		[apiCall, run],
	);

	const editThenExport = useCallback(
		(entry: EditPayload) => {
			let editSucceeded = false;
			run({
				ids: [entry.id],
				clearFailedExportIds: [entry.id],
				mutationFn: async () => {
					const editRequest = editRequestFor(entry);
					const editResult = await apiCall({
						endpoint: editRequest.endpoint,
						method: "PATCH",
						queryParams: AFFECTED_ENTRY_GROUPS_QUERY,
						body: editRequest.body,
						headers: actingHeaders(entry.timekeeperId),
					});
					editSucceeded = true;
					try {
						const exportedEntryGroupIds = await executeExport(
							[{ id: entry.id, entryGroupId: entry.entryGroupId }],
							entry.timekeeperId,
						);
						return { exportedEntryGroupIds };
					} catch (err) {
						const exportedEntryGroupIds =
							err instanceof EntryMutationOutcomeError
								? err.exportedEntryGroupIds
								: [];
						throw new EntryMutationOutcomeError(
							err instanceof Error ? err.message : "Export failed",
							{ committedResult: editResult, exportedEntryGroupIds },
						);
					}
				},
				toast: {
					loading: "Saving and exporting…",
					success: "Entry saved and exported",
					error: (err) =>
						err instanceof EntryMutationOutcomeError
							? "Entry saved but export failed"
							: "Failed to save entry",
				},
				onError: () => {
					if (editSucceeded) {
						void queryClient.invalidateQueries({
							queryKey: entryGroupsQueryKey,
						});
					}
				},
			});
		},
		[apiCall, executeExport, queryClient, run],
	);

	const deleteEntries = useCallback(
		(ids: number[], timekeeperId?: string | null) => {
			const n = ids.length;
			run({
				ids,
				clearFailedExportIds: ids,
				mutationFn: () =>
					apiCall({
						endpoint: "/v2/entry-groups",
						method: "PATCH",
						queryParams: AFFECTED_ENTRY_GROUPS_QUERY,
						body: {
							data: entryGroupsForLegacyIds(ids).map((id) => ({
								id,
								status: "deleted",
							})),
						},
						headers: actingHeaders(timekeeperId),
					}),
				toast: {
					loading: `Deleting ${n} ${plural(n)}…`,
					success: `Deleted ${n} ${plural(n)}`,
					error: (err) => {
						if (err instanceof ApiError) {
							if (err.code === "ENTRY_EXPORTED")
								return "Some entries are exported and can't be deleted";
							if (err.code === "ENTRY_NOT_FOUND")
								return "One or more entries no longer exist";
						}
						return "Failed to delete entries";
					},
				},
			});
		},
		[apiCall, entryGroupsForLegacyIds, run],
	);

	const duplicateEntries = useCallback(
		(ids: number[], timekeeperId?: string | null) => {
			const n = ids.length;
			run({
				ids,
				mutationFn: () =>
					apiCall({
						endpoint: "/v2/entry-groups",
						method: "POST",
						queryParams: AFFECTED_ENTRY_GROUPS_QUERY,
						body: {
							data: entryGroupsForLegacyIds(ids).map((id) => ({
								duplicate: { entryGroupIds: [id] },
							})),
						},
						headers: actingHeaders(timekeeperId),
					}),
				toast: {
					loading: `Duplicating ${n} ${plural(n)}…`,
					success: `Duplicated ${n} ${plural(n)}`,
					error: (err) =>
						err instanceof ApiError && err.code === "ENTRY_NOT_FOUND"
							? "One or more entries no longer exist"
							: "Failed to duplicate entries",
				},
			});
		},
		[apiCall, entryGroupsForLegacyIds, run],
	);

	const actionFromSources = useCallback(
		(
			kind: "group" | "merge" | "collapse",
			ids: number[],
			body: Record<string, unknown>,
			onApiError?: (err: ApiErrorType) => void,
			timekeeperId?: string | null,
		) => {
			run({
				ids,
				mutationFn: async () => {
					try {
						const groupIds = entryGroupsForLegacyIds(ids);
						return await apiCall({
							endpoint: "/v2/entry-groups",
							method: "POST",
							queryParams: AFFECTED_ENTRY_GROUPS_QUERY,
							body: {
								data: [
									kind === "group"
										? { entryIds: entryIdsForLegacyIds(ids), ...body }
										: {
												type: kind,
												sourceEntryGroupIds: groupIds,
												...body,
											},
								],
							},
							headers: actingHeaders(timekeeperId),
						});
					} catch (err) {
						if (err instanceof ApiError) onApiError?.(err);
						throw err;
					}
				},
				toast: {
					loading: `${kind === "group" ? "Grouping" : kind === "merge" ? "Merging" : "Collapsing"} ${ids.length} entries…`,
					success: `${kind === "group" ? "Grouped" : kind === "merge" ? "Merged" : "Collapsed"} ${ids.length} entries`,
					error: `Failed to ${kind} entries`,
				},
			});
		},
		[apiCall, entryGroupsForLegacyIds, entryIdsForLegacyIds, run],
	);

	const groupEntries = useCallback(
		(
			ids: number[],
			options: GroupOptions,
			onApiError?: (err: ApiErrorType) => void,
			timekeeperId?: string | null,
		) =>
			actionFromSources(
				"group",
				ids,
				{ ...stripUndefined(options) },
				onApiError,
				timekeeperId,
			),
		[actionFromSources],
	);

	const mergeEntries = useCallback(
		(
			ids: number[],
			options: MergeOptions,
			onApiError?: (err: ApiErrorType) => void,
			timekeeperId?: string | null,
		) =>
			actionFromSources(
				"merge",
				ids,
				{ ...stripUndefined(options) },
				onApiError,
				timekeeperId,
			),
		[actionFromSources],
	);

	const collapseEntries = useCallback(
		(
			ids: number[],
			mainSourceId: number,
			options: CollapseOptions,
			onApiError?: (err: ApiErrorType) => void,
			timekeeperId?: string | null,
		) => {
			const primaryEntryGroupId = entryGroupsForLegacyIds([mainSourceId])[0];
			actionFromSources(
				"collapse",
				ids,
				{
					...(primaryEntryGroupId ? { primaryEntryGroupId } : {}),
					...stripUndefined(options),
				},
				onApiError,
				timekeeperId,
			);
		},
		[actionFromSources, entryGroupsForLegacyIds],
	);

	const moveEntriesToNewGroup = useCallback(
		(entries: Entry[], timekeeperId?: string | null) => {
			const ids = entries.map((entry) => entry.id);
			const sources = entryGroupRowsForEntries(entries).flatMap((parent) =>
				parent.miniTransactions.map((mini) => ({ parent, mini })),
			);
			const sourceIds = uniqueSelectedSourceIds(sources);
			if (sourceIds.length === 0) {
				toast.error("Selected entries are no longer available");
				return;
			}
			run({
				ids,
				mutationFn: () =>
					apiCall({
						endpoint: "/v2/entry-groups",
						method: "POST",
						queryParams: AFFECTED_ENTRY_GROUPS_QUERY,
						body: {
							data: [
								{
									status: "tracked",
									type: "group",
									entryIds: sourceIds,
									...stripUndefined(resolveNewEntryGroupFields(sources)),
								},
							],
						},
						headers: actingHeaders(timekeeperId),
					}),
				toast: {
					loading: `Moving ${sourceIds.length} ${
						sourceIds.length === 1 ? "entry" : "entries"
					} to a new group…`,
					success: `Moved ${sourceIds.length} ${
						sourceIds.length === 1 ? "entry" : "entries"
					} to a new group`,
					error: "Failed to move selection to a new group",
				},
			});
		},
		[apiCall, entryGroupRowsForEntries, run],
	);

	const exportEntries = useCallback(
		(entries: ExportTarget[], timekeeperId?: string | null) => {
			const n = entries.length;
			const ids = entries.map((entry) => entry.id);
			run({
				ids,
				clearFailedExportIds: ids,
				mutationFn: async () => ({
					exportedEntryGroupIds: await executeExport(entries, timekeeperId),
				}),
				toast: {
					loading: `Exporting ${n} ${plural(n)}…`,
					success: `Exported ${n} ${plural(n)}`,
					error: (err) => {
						if (err instanceof ApiError) {
							if (err.code === "ENTRY_EXPORTED")
								return "Some entries are already exported";
							if (err.code === "ENTRY_DELETED")
								return "Deleted entries can't be exported";
							if (err.code === "ENTRY_NOT_FOUND")
								return "One or more entries no longer exist";
						}
						return err instanceof Error
							? err.message
							: "Failed to export entries";
					},
				},
			});
		},
		[executeExport, run],
	);

	const setStatusMany = useCallback(
		(
			ids: number[],
			target: SetStatusTarget,
			onApiError?: (err: ApiErrorType) => void,
			timekeeperId?: string | null,
		) => {
			if (ids.length === 0) return;
			const n = ids.length;
			const verbing =
				target === "deleted"
					? "Deleting"
					: target === "reviewed"
						? "Marking reviewed"
						: "Marking tracked";
			const verbed =
				target === "deleted"
					? "Deleted"
					: target === "reviewed"
						? "Marked reviewed"
						: "Marked tracked";
			run({
				ids,
				clearFailedExportIds: ids,
				mutationFn: async () => {
					try {
						return await apiCall({
							endpoint: "/v2/entry-groups",
							method: "PATCH",
							queryParams: AFFECTED_ENTRY_GROUPS_QUERY,
							body: {
								data: entryGroupsForLegacyIds(ids).map((id) => ({
									id,
									status: target,
								})),
							},
							headers: actingHeaders(timekeeperId),
						});
					} catch (err) {
						if (err instanceof ApiError) onApiError?.(err);
						throw err;
					}
				},
				toast: {
					loading: `${verbing} ${n} ${plural(n)}…`,
					success: `${verbed} ${n} ${plural(n)}`,
					error: (err) => {
						if (err instanceof ApiError) {
							if (err.code === "ENTRY_EXPORTED")
								return "Some entries are exported — nothing was changed";
							if (err.code === "ENTRY_NOT_FOUND")
								return "Some entries no longer exist — nothing was changed";
						}
						return target === "deleted"
							? "Failed to delete entries"
							: "Failed to update entries";
					},
				},
			});
		},
		[apiCall, entryGroupsForLegacyIds, run],
	);

	const setStatus = useCallback(
		(
			id: number,
			target: SetStatusTarget,
			onApiError?: (err: ApiErrorType) => void,
			timekeeperId?: string | null,
		) => {
			setStatusMany([id], target, onApiError, timekeeperId);
		},
		[setStatusMany],
	);

	const resolveFeedback = useCallback(
		(entryId: number) => {
			run({
				ids: [entryId],
				mutationFn: async () => {
					throw legacyRouteStubError(
						`/records/${entryId}/resolve-feedback`,
						"/v2/entry-groups/{entryGroupId}",
					);
				},
				toast: {
					loading: "Resolving feedback…",
					success: "Feedback resolved",
					error: "Needs to be wired to /v2/entry-groups/{entryGroupId}",
				},
			});
		},
		[run],
	);

	const value = useMemo<ActionsContextType>(
		() => ({
			deleteEntries,
			editEntry,
			editThenExport,
			addEntry,
			isAddingEntry,
			duplicateEntries,
			groupEntries,
			mergeEntries,
			collapseEntries,
			moveEntriesToNewGroup,
			exportEntries,
			resolveFeedback,
			setStatus,
			setStatusMany,
			failedExports,
			pendingEntryIds,
			queueSize: pendingEntryIds.size,
		}),
		[
			addEntry,
			isAddingEntry,
			collapseEntries,
			deleteEntries,
			duplicateEntries,
			editEntry,
			editThenExport,
			exportEntries,
			failedExports,
			groupEntries,
			mergeEntries,
			moveEntriesToNewGroup,
			pendingEntryIds,
			resolveFeedback,
			setStatus,
			setStatusMany,
		],
	);

	return (
		<ActionsContext.Provider value={value}>{children}</ActionsContext.Provider>
	);
}

export function useActions() {
	const ctx = useContext(ActionsContext);
	if (!ctx)
		throw new Error("useActions must be used within EntriesActionsProvider");
	return ctx;
}
